/**
 * ProfileSetup — 5-step mandatory onboarding wizard (Feb 2026 rework).
 *
 * The old 3-step flow was extended to include mandatory email + phone
 * verification gates. New users get `onboarding_v2_required: true` at
 * signup; the server enforces every gate in POST /api/profile/setup so
 * a bypassed client can't skip them.
 *
 *   Step 0 · About You         → StepAboutYou    (photos ≥ 3, name, age, gender)
 *   Step 1 · Location          → StepLocation    (city required, prompts browser geo)
 *   Step 2 · Verify Email      → StepVerifyEmail (auto-OTP unless already verified)
 *   Step 3 · Verify Phone      → StepVerifyPhone (Twilio OTP unless already verified)
 *   Step 4 · Compatibility     → StepCompatibility (optional; final submit)
 *
 * The Compatibility step's Continue button doubles as the "Complete
 * Profile" submit (see `isLastStep` prop).
 */
import { useState } from 'react';
import { ArrowLeft, X, Eye } from 'lucide-react';
import useProfileHydration from '@/hooks/useProfileHydration';
import StepAboutYou from '@/components/setup/StepAboutYou';
import StepCompatibility from '@/components/setup/StepCompatibility';
import StepLocation from '@/components/setup/StepLocation';
import StepVerifyEmail from '@/components/setup/StepVerifyEmail';
import StepVerifyPhone from '@/components/setup/StepVerifyPhone';
import PhotoCropperDialog from '@/components/PhotoCropperDialog';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import FullProfileBody from '@/components/FullProfileBody';

const TOTAL_STEPS_NEW = 5;
const TOTAL_STEPS_EDIT = 3;
const STEP_TITLES_NEW = ['About You', 'Location', 'Verify Email', 'Verify Phone', 'Compatibility'];
const STEP_TITLES_EDIT = ['About You', 'Location', 'Compatibility'];

export default function ProfileSetup() {
  const [previewOpen, setPreviewOpen] = useState(false);
  const H = useProfileHydration();
  const {
    isExplicitEdit,
    profile, setProfile, step, setStep,
    loading, uploading, cropSrc, setCropSrc,
    fileRef,
    questions,
    promptsList, interestsList, interestsCategories, promptsCategories, orientationsList, religionsList, educationList,
    industriesList, languagesList, personalityList, zodiacList, exerciseList, politicsList,
    uploadPhoto, onCropComplete, removePhotoByPath, reorderPhotos, getUrl,
    submit, exitOnboarding, detectLoc,
  } = H;

  const TOTAL_STEPS = isExplicitEdit ? TOTAL_STEPS_EDIT : TOTAL_STEPS_NEW;
  const STEP_TITLES = isExplicitEdit ? STEP_TITLES_EDIT : STEP_TITLES_NEW;
  // In edit mode we render About You (0) → Location (1) → Compatibility (2).
  // Skip Verify Email / Verify Phone entirely — those live in Settings for
  // existing users. In new-signup mode all 5 steps are enforced.
  const stepIsCompatibility = step === (isExplicitEdit ? 2 : 4);
  const stepIsLocation = step === 1;
  const stepIsAboutYou = step === 0;
  const stepIsVerifyEmail = !isExplicitEdit && step === 2;
  const stepIsVerifyPhone = !isExplicitEdit && step === 3;

  // Per-step progress weight (each step is worth an equal share of the bar).
  const answered = Object.keys(profile.answers).length;
  const stepShare = 100 / TOTAL_STEPS;
  const stepBase = step * stepShare;
  const stepFill =
    stepIsAboutYou ? Math.min(stepShare,
        (profile.display_name ? 4 : 0)
      + (profile.age ? 4 : 0)
      + (profile.gender ? 4 : 0)
      + Math.min(profile.photos.length, 3) * 2 + 2)
    : stepIsLocation ? (profile.location.city ? stepShare : 4)
    : stepIsCompatibility ? Math.min(stepShare, (answered / 10) * stepShare)
    : stepShare / 2; // verification steps show mid-progress until confirmed
  const progress = Math.min(100, stepBase + stepFill);
  const stepsLeft = TOTAL_STEPS - 1 - step;
  const motivation =
      progress < 25 ? "Let's get started — takes under 2 minutes"
    : progress < 50 ? 'Looking good, keep going!'
    : progress < 75 ? "Halfway there — you're crushing it"
    : progress < 95 ? 'Almost done, last few details'
                    : 'Ready to meet your matches!';

  return (
    <div className="min-h-screen bg-white" data-testid="profile-setup">
      {/* Sticky progress header */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-xl border-b border-black/[0.06]">
        <div className="max-w-xl mx-auto px-5 py-3">
          <div className="flex items-center gap-3 mb-2">
            {step > 0 ? (
              <button
                onClick={() => setStep((s) => s - 1)}
                data-testid="back-step"
                aria-label="Back"
                className="w-8 h-8 rounded-full border border-[var(--hairline-strong)] text-[#4B4B52] hover:bg-black/[0.02] flex items-center justify-center transition"
              >
                <ArrowLeft size={15} />
              </button>
            ) : (
              <button
                onClick={exitOnboarding}
                data-testid="exit-onboarding"
                aria-label="Exit"
                className="w-8 h-8 rounded-full border border-[var(--hairline-strong)] text-[#4B4B52] hover:bg-black/[0.02] flex items-center justify-center transition"
              >
                <X size={15} />
              </button>
            )}
            <div className="flex-1 min-w-0 text-center">
              <p className="text-[10px] font-bold tracking-[0.18em] uppercase text-[#8A8A93]">
                Step {step + 1} of {TOTAL_STEPS} · {STEP_TITLES[step]}
              </p>
            </div>
            {isExplicitEdit && (
              <button
                onClick={() => setPreviewOpen(true)}
                data-testid="preview-profile-btn"
                aria-label="Preview profile"
                className="w-8 h-8 rounded-full border border-[var(--hairline-strong)] text-[#0F0F10] hover:bg-black/[0.04] flex items-center justify-center transition mr-1.5"
                title="Preview how others see your profile"
              >
                <Eye size={15} />
              </button>
            )}
            <span className="w-12 text-right text-[12px] font-bold tabular-nums text-[#B91C1C]" data-testid="progress-pct">
              {Math.round(progress)}%
            </span>
          </div>
          <div className="h-1.5 bg-black/[0.05] rounded-full overflow-hidden">
            <div className="h-full brand-gradient rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
          </div>
          <p className="text-[11px] text-[#4B4B52] mt-1.5 text-center" data-testid="progress-motivation">
            {motivation}{stepsLeft > 0 && progress < 95 ? ` · ${stepsLeft} step${stepsLeft === 1 ? '' : 's'} left` : ''}
          </p>
        </div>
      </div>

      <div className="max-w-xl mx-auto px-5 pt-32 pb-24">
        {stepIsAboutYou && (
          <StepAboutYou
            profile={profile}
            setProfile={setProfile}
            fileRef={fileRef}
            uploadPhoto={uploadPhoto}
            uploading={uploading}
            reorderPhotos={reorderPhotos}
            removePhotoByPath={removePhotoByPath}
            getUrl={getUrl}
            orientationsList={orientationsList}
            promptsList={promptsList}
            promptsCategories={promptsCategories}
            interestsList={interestsList}
            interestsCategories={interestsCategories}
            religionsList={religionsList}
            industriesList={industriesList}
            educationList={educationList}
            languagesList={languagesList}
            personalityList={personalityList}
            zodiacList={zodiacList}
            exerciseList={exerciseList}
            politicsList={politicsList}
            isEditMode={isExplicitEdit}
            onNext={() => setStep(1)}
          />
        )}

        {stepIsLocation && (
          <StepLocation
            profile={profile}
            setProfile={setProfile}
            detectLoc={detectLoc}
            onNext={() => setStep(2)}
          />
        )}

        {stepIsVerifyEmail && (
          <StepVerifyEmail onNext={() => setStep(3)} />
        )}

        {stepIsVerifyPhone && (
          <StepVerifyPhone onNext={() => setStep(4)} />
        )}

        {stepIsCompatibility && (
          <StepCompatibility
            profile={profile}
            setProfile={setProfile}
            questions={questions}
            isLastStep
            submitting={loading}
            onSubmit={submit}
          />
        )}
      </div>

      <PhotoCropperDialog
        open={!!cropSrc}
        imageSrc={cropSrc}
        onCropped={onCropComplete}
        onCancel={() => setCropSrc(null)}
        uploading={uploading}
      />

      {/* Live preview of how the profile looks to matchmakers (edit mode only). */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent
          data-testid="profile-preview-dialog"
          className="max-w-md p-0 overflow-hidden bg-white border-0 rounded-3xl"
        >
          <div className="max-h-[85vh] overflow-y-auto p-5">
            <div className="flex items-center justify-between mb-4 pb-3 border-b border-black/[0.06]">
              <span className="text-[11px] font-[800] tracking-[0.6px] uppercase text-[#6B7280]">
                Preview · How others see you
              </span>
              <button
                onClick={() => setPreviewOpen(false)}
                data-testid="profile-preview-close"
                className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-black/[0.04] transition-colors"
                aria-label="Close preview"
              >
                <X size={16} />
              </button>
            </div>
            <FullProfileBody profile={profile} distanceKm={null} />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
